import { AxiosHeaders, AxiosResponseHeaders } from 'axios';

export const LOCAL_STORAGE_ELEMENT_ID = 'LOCAL_STORAGE_ELEMENT_ID';

export const getElementID = (pageName: string, elementName: string): string => {
  const id: string = pageName + (elementName ? '-' + elementName : '');
  if (window.localStorage.getItem(LOCAL_STORAGE_ELEMENT_ID)) {
    console.log("Element ID: document.querySelector('#" + id + "');");
  }
  return id;
};

type ClassObject = Record<string, boolean | undefined | null>;

type ClassValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | ClassObject
  | ClassValue[];

const toVal = (mix: ClassValue): string | undefined => {
  if (typeof mix === 'string' || typeof mix === 'number') {
    return mix.toString();
  } else if (Array.isArray(mix)) {
    return mix
      .filter(
        (value): value is Exclude<ClassValue, boolean | null | undefined> =>
          !!value
      )
      .map(toVal)
      .filter((value) => !!value)
      .join(' ');
  } else if (mix && typeof mix === 'object') {
    return Object.entries(mix)
      .filter(([_, value]) => !!value)
      .map(([key]) => key)
      .join(' ');
  }
  return undefined;
};

export const clsx = (...classNames: ClassValue[]): string => {
  const filteredClasses = classNames.filter(
    (value) => value !== null && value !== undefined && value !== false
  );

  const combinedClasses = filteredClasses
    .map(toVal)
    .filter((value) => !!value)
    .join(' ');

  return combinedClasses || '';
};

export const isiPhone = () => {
  const toMatch = [/iPhone/i, /iPad/i, /iPod/i];

  return toMatch.some((toMatchItem) => {
    return navigator.userAgent.match(toMatchItem);
  });
};

export const isMobile = () => {
  const toMatch = [
    /Android/i,
    /webOS/i,
    /iPhone/i,
    /iPad/i,
    /iPod/i,
    /BlackBerry/i,
    /Windows Phone/i,
  ];

  return toMatch.some((toMatchItem) => {
    return navigator.userAgent.match(toMatchItem);
  });
};

export const iOS = () => {
  return (
    [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod',
    ].includes(navigator.platform) ||
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
};

export const dispatchToAllYouTubeVideos = (
  command: 'pauseVideo' | 'stopVideo' | 'playVideo',
  idToExclude?: string
) => {
  const iframes = document.querySelectorAll('iframe');
  Array.from(iframes).forEach((f: HTMLIFrameElement) => {
    if (
      (!idToExclude || f.id !== idToExclude) &&
      typeof f.contentWindow?.postMessage === `function`
    ) {
      f.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func: command, args: '' }),
        '*'
      );
    }
  });
};

export const dispatchToYoutubeVideo = (
  command: 'pauseVideo' | 'stopVideo' | 'playVideo',
  id?: string
) => {
  const frame = document.querySelector(`#${id}`) as HTMLIFrameElement;
  if (frame) {
    frame.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func: command, args: '' }),
      '*'
    );
  }
};

export const formatData = (input: string) => {
  const value = String(input)
    .replace(/\D/g, '') // Retira o que não é número
    .slice(0, 8) // Limita o tamanho
    .replace(/(\d{2})(\d{1,2})/, '$1/$2') // Coloca a primeira barra
    .replace(/(\d{2}\/\d{2})(\d{1,2})/, '$1/$2'); // Coloca a segunda barra

  const dateParts = value.split('/');
  const year = parseInt(dateParts[2], 10);
  if (year > 9999) {
    return '';
  }

  return value;
};

export const formataStringData = (data: string) => {
  let dia = data.split('/')[0];
  let mes = data.split('/')[1];
  let ano = data.split('/')[2];

  return ano + '-' + ('0' + mes).slice(-2) + '-' + ('0' + dia).slice(-2);
};

export const formatHora = (input: string) => {
  const value = String(input)
    .replace(/\D/g, '') // Retira o que não é número
    .slice(0, 4) // Limita o tamanho
    .replace(/(\d{2})(\d{1,2})/, '$1:$2'); // Coloca os dois pontos
  return value;
};

export const returnDateAndHour = (
  dataHoraOriginal: string
): {
  formatedDate: string;
  formatedHour: string;
} => {
  const data = dataHoraOriginal ? new Date(dataHoraOriginal) : null;
  const formatedDate = data
    ? `${data.getDate().toString().padStart(2, '0')}/${(data.getMonth() + 1)
        .toString()
        .padStart(2, '0')}/${data.getFullYear()}`
    : '';
  const formatedHour = data
    ? `${data.getHours().toString().padStart(2, '0')}:${data
        .getMinutes()
        .toString()
        .padStart(2, '0')}`
    : '';
  return { formatedDate, formatedHour };
};

export const downloadFileFromArrayBuffer = (
  buffer: ArrayBuffer,
  fileName: string
) => {
  const blob = new Blob([buffer], {
    type: 'octet/stream',
  });
  downloadFileFromBlobOrFile(blob, fileName);
};

export const downloadFileFromBlobOrFile = (
  file: File | Blob,
  fileName?: string
) => {
  let link = document.createElement('a');
  link.href = window.URL.createObjectURL(file);
  link.download = fileName ?? (file as File)?.name ?? '-';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const getFileNameFromContentDispositionOrDefault = (
  headers: Partial<AxiosResponseHeaders>,
  defaultName: string
) => {
  const contentDisposition =
    headers && typeof headers.get === 'function'
      ? headers.get('Content-Disposition')
      : undefined;
  return contentDisposition
    ? getFileNameFromContentDisposition(contentDisposition)
    : defaultName;
};

export const getFileNameFromContentDisposition = (
  contentDisposition?: string | number | boolean | string[] | AxiosHeaders
) => {
  return (
    Array.isArray(contentDisposition)
      ? contentDisposition.join(',')
      : String(contentDisposition)
  ).replace(/(.*?)filename=(.*?)(;(.*?)$|$)/im, '$2');
};

export const objectKeysFiltered = <Type>(
  obj: object,
  keysToExclude: string[]
): Type => {
  const p = { ...obj } as any;
  for (let k of keysToExclude) {
    delete p[k];
  }
  return p as Type;
};

export const isNullOrUndefined = <T>(value: T | null | undefined): boolean =>
  value === null || value === undefined;
